import { createContext, useContext, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { settingsService, profileService, type Setting } from '@/services';

interface SettingsContextType {
  settings: Setting[];
  isLoading: boolean;
  getSetting: <T = unknown>(key: string) => T | null;
  getSettingRecord: (key: string) => Setting | undefined;
  invalidateCache: () => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await profileService.get(user.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  const companyId = profile?.company_id;

  const { data: settingsData, isLoading } = useQuery({
    queryKey: ['settings-cache', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      const result = await settingsService.list(companyId);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  });

  const settings = settingsData || [];

  const getSettingRecord = useCallback((key: string): Setting | undefined => {
    return settings.find(s => s.setting_key === key);
  }, [settings]);

  const getSetting = useCallback(<T = unknown,>(key: string): T | null => {
    const record = settings.find(s => s.setting_key === key);
    if (!record) return null;
    const value = record.setting_value ?? record.default_value;
    return value as T | null;
  }, [settings]);

  const invalidateCache = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['settings-cache', companyId] });
  }, [queryClient, companyId]);

  return (
    <SettingsContext.Provider value={{ settings, isLoading, getSetting, getSettingRecord, invalidateCache }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
